import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Brain, 
  Users, 
  TrendingUp, 
  Shield, 
  MessageSquare,
  ChevronRight,
  Play,
  Check,
  Star,
  ArrowRight,
  Sparkles,
  BarChart3,
  Target,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Predictions',
    description: 'Predict dropout risk, recovery delays, and plateaus before they happen with 87% accuracy.',
    color: 'bg-primary-light text-primary'
  },
  {
    icon: TrendingUp,
    title: 'Smart Compliance Engine',
    description: 'Automated reminders that adapt to patient behavior, increasing compliance by 52%.',
    color: 'bg-success-light text-success'
  },
  {
    icon: MessageSquare,
    title: 'AI Companion Chat',
    description: '24/7 patient support that answers questions, encourages adherence, and escalates concerns.',
    color: 'bg-info-light text-info'
  },
  {
    icon: BarChart3,
    title: 'Real-time Analytics',
    description: 'Track KPIs across all services with actionable insights for better outcomes.',
    color: 'bg-warning-light text-warning'
  },
  {
    icon: Users,
    title: 'Multi-Role Platform',
    description: 'Unified experience for admins, therapists, and patients with role-specific dashboards.',
    color: 'bg-accent text-accent-foreground'
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'HIPAA-compliant with full audit trails, consent management, and AI governance.',
    color: 'bg-secondary text-secondary-foreground'
  }
];

const stats = [
  { value: '58%', label: 'LTV Improvement' },
  { value: '52%', label: 'Dropout Reduction' },
  { value: '17%', label: 'Better Outcomes' },
  { value: '4.2 mo', label: 'Payback Period' },
];

const testimonials = [
  {
    quote: "Physiomobile's AI has transformed how we manage patient care. Dropout rates dropped by half within 3 months.",
    author: 'Dr. Ahmad Rahman',
    role: 'Chief Medical Officer',
    company: 'KL Medical Group',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'
  },
  {
    quote: "The AI Copilot saves me hours every week. I can focus on patients while it handles the paperwork and predictions.",
    author: 'Dr. Lisa Chen',
    role: 'Senior Physiotherapist',
    company: 'Wellness First',
    avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100'
  },
  {
    quote: "Finally, a healthcare platform that understands rehabilitation. The compliance reminders alone are worth it.",
    author: 'Sarah Mitchell',
    role: 'Operations Director',
    company: 'RehabCare Network',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'
  }
];

const services = [
  'Physiotherapy',
  'Occupational Therapy',
  'Speech Therapy',
  'Diet & Nutrition',
  'Care & Companion'
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display font-bold text-xl text-foreground">Physiomobile</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
              <a href="#services" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Services</a>
              <a href="#testimonials" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Testimonials</a>
              <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin">Login</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/admin">Request Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-light/30 to-background" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-info/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="outline" className="mb-6 px-4 py-1.5 text-sm">
                <Sparkles className="w-3 h-3 mr-2 text-primary" />
                AI-Native Healthcare Operating System
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
                The Future of
                <span className="text-primary"> Rehab & Extended Care</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Physiomobile is an AI-powered ecosystem that increases compliance, predicts recovery trajectories, improves outcomes, and reduces churn — all while optimizing therapist operations.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button size="lg" className="gap-2 px-8" asChild>
                  <Link to="/admin">
                    Explore Admin Dashboard
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="gap-2" asChild>
                  <Link to="/therapist">
                    <Play className="w-4 h-4" />
                    See Therapist View
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
          
          {/* Hero Image/Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-16 relative"
          >
            <div className="relative mx-auto max-w-5xl">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-info/20 to-success/20 rounded-3xl blur-2xl opacity-50" />
              <div className="relative bg-card rounded-2xl border border-border shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
                  <div className="w-3 h-3 rounded-full bg-primary/60" />
                  <div className="w-3 h-3 rounded-full bg-warning/60" />
                  <div className="w-3 h-3 rounded-full bg-success/60" />
                  <span className="ml-2 text-xs text-muted-foreground">AI Command Center</span>
                </div>
                <div className="p-6 grid grid-cols-3 gap-4">
                  {/* Mini dashboard preview */}
                  <div className="col-span-2 space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      {['2,847 Patients', '78.4% Compliance', '47 At Risk'].map((stat, idx) => (
                        <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Metric</p>
                          <p className="font-semibold text-foreground">{stat}</p>
                        </div>
                      ))}
                    </div>
                    <div className="h-32 bg-muted/30 rounded-lg flex items-center justify-center">
                      <div className="flex items-end gap-1">
                        {[40, 55, 45, 70, 65, 85, 78].map((h, i) => (
                          <div key={i} className="w-6 bg-primary/60 rounded-t" style={{ height: `${h}%` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-primary-light/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-xs font-medium">AI Alert</span>
                      </div>
                      <p className="text-xs text-muted-foreground">23 patients need attention</p>
                    </div>
                    <div className="p-3 bg-success-light/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-success" />
                        <span className="text-xs font-medium">Improvement</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Compliance +15%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl sm:text-5xl font-display font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section id="services" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Multi-Service Platform</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              One Platform, Multiple Services
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Manage all your rehab and extended care services from a single AI-powered platform.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center">
                      <Heart className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium text-foreground">{service}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Powerful Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              AI That Actually Helps
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our AI doesn't just analyze — it acts. From predicting risks to automating follow-ups, every feature is designed to improve outcomes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                      <feature.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-display font-semibold text-lg text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Role Demos */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Try It Now</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Experience Every Role
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore our fully functional demo dashboards for each user type.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Admin / Operations', subtitle: 'AI Command Center', link: '/admin', color: 'bg-primary', icon: BarChart3 },
              { title: 'Therapist / Professional', subtitle: 'AI Copilot', link: '/therapist', color: 'bg-info', icon: Target },
              { title: 'Patient / Customer', subtitle: 'AI Companion', link: '/patient', color: 'bg-success', icon: Heart },
            ].map((role, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={role.link}>
                  <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer overflow-hidden">
                    <CardContent className="p-0">
                      <div className={`${role.color} p-8 text-center`}>
                        <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                          <role.icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-display font-semibold text-lg text-white mb-1">{role.title}</h3>
                        <p className="text-white/80 text-sm">{role.subtitle}</p>
                      </div>
                      <div className="p-4 flex items-center justify-center gap-2 text-primary group-hover:text-primary/80">
                        <span className="text-sm font-medium">Explore Dashboard</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Trusted by Healthcare Leaders
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-warning text-warning" />
                      ))}
                    </div>
                    <p className="text-muted-foreground mb-6 flex-1">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={testimonial.avatar} />
                        <AvatarFallback>{testimonial.author.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground text-sm">{testimonial.author}</p>
                        <p className="text-xs text-muted-foreground">{testimonial.role}, {testimonial.company}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Ready to Transform Your Healthcare Operations?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join leading healthcare providers who've improved patient outcomes and reduced costs with Physiomobile's AI platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="gap-2 px-8" asChild>
                <Link to="/admin">
                  Start Free Trial
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="gap-2">
                Schedule Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Activity className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-display font-bold text-foreground">Physiomobile</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-foreground transition-colors">Contact</a>
            </div>
            
            <p className="text-sm text-muted-foreground">
              © 2024 Physiomobile. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
